import type { ShellDockGroup, ShellGeometryChangeOptions, ShellMicState, ShellPendingChatMessage } from "@smartmanager/shell-ui/host";

/** A row Done took out of the notification feed list, and where it stood. */
export type DismissedNotificationEntry = { readonly key: string; readonly index: number; readonly entry: unknown };

export type PassageAuthEnvValuesType = {
appId?: string;
  apiKey?: string;
};

export type EnvironmentType = "development" | "production";

/** Two windows docked side by side; the shell owns the shape. */
export type ComponentDockGroup = ShellDockGroup;

/** How a window move or resize reached the store; the shell owns the shape. */
export type GeometryChangeOptions = ShellGeometryChangeOptions;

export type componentsMapType = {
  // content: any;
  id: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  priority: string;
  name: string;
  group: string | null;
  props: any;
  createdDate: Date;
  deletedDate: string | Date | null;
  description?: string; //enforced for users, not public
  score?: number; //enforced for users, not public
  reason?: string; //enforced for users, not public
  isMinimized?: boolean; //true when component is minimized to make space
  minimizedScale?: number; //scale factor (0.3-0.6) when minimized
  /** True when the system tucked the component away to relieve viewport pressure. */
  isSystemTuckedAway?: boolean;
  // Dependency tracking for parent-child component relationships
  parentComponentId?: string; // ID of the parent component that spawned this one
  dependencyIds?: string[]; // IDs of components that depend on this one (children)
  zIndex?: number; // Custom z-index for layering (dependencies get parent zIndex - 1)
  relationVisual?: "search-brief" | "search-images" | "search-videos";
  dependencyPlacement?: "right-top" | "right-bottom";
  /** Component identity (packages/shell-ui/src/features/theme/families.ts); derived from `name` when absent. */
  family?: import("@smartmanager/shell-ui/features/theme/families").ComponentFamily;
  // Atomic peer docking is independent from retrieval/media dependencies.
  dockGroup?: ComponentDockGroup;
  // Dependency spread/collapse state
  isDependenciesSpread?: boolean; // On parent: true when dependencies are spread across screen
  originalDependencyPosition?: { x: number; y: number }; // On dependency: stores original position before spread
  isSpreadDependency?: boolean; // On dependency: true when this is currently spread (for animation)
  // Animation state (transient, not persisted)
  isAnimatingPosition?: boolean; // True during position animation transitions
  animationStartTime?: number; // Timestamp when animation started
  isRestoringFromMinimized?: boolean; // True during restoration from minimized state
  restorationStartTime?: number; // Timestamp when restoration started
  // Mobile layout support - separate position/size for mobile view
  // When in mobile mode, these are used instead of position/size
  // If not set, falls back to regular position/size (for backwards compatibility)
  mobilePosition?: { x: number; y: number };
  mobileSize?: { width: number; height: number };
  /** True once the user resized the window by hand; content-fit leaves its height alone. */
  userSized?: boolean;
};

export type ComponentMapReferenceType = {
  id: string;
  name: string;
  reason?: string;
  description?: string;
  score?: number;
  createdDate?: Date | number | string;
  title?: string;
  customizesPrimitive?: string | null;
  version?: number;
  status?: string;
};

export type SyncVendorProp = {
  token?: string;
  /** Provider-issued application credential for import-only reads (encrypted at rest by the token-state policy). */
  appSecret?: string;
  refreshToken?: string;
  expiresIn?: number;
  accountId?: string;
  provider?: string;
  providerAccountKey?: string;
  providerAccountId?: string;
  providerTenantIds?: string[];
  providerMetadata?: Record<string, unknown>;
  configurationGeneration?: string;
  credentialGeneration?: string;
  grantedScopeFingerprint?: string;
  accountIdentity?: {
    kind?: "provider_account" | "workspace" | "bot" | "phone" | "signed_helper";
    stableId?: string;
    providerAccountId?: string;
    fingerprint?: string;
  };
  accountEmail?: string;
  accountName?: string;
  connectedAt?: number;
  updatedAt?: number;
  configurationUpdatedAt?: number;
  lastSynced: number;
  lastSyncedAt?: number;
  lastSyncedByType?: Record<string, number>;
  contactCursor?: string;
  pendingContactCursor?: string;
  contactCursorOrder?: number;
  pendingContactCursorOrder?: number;
  pendingContactCursorGeneration?: string;
  /** Account configuration the legacy calendar cleanup fields below belong to. */
  legacyCalendarCleanupConfigurationGeneration?: string;
  /** Legacy calendar duplicate cleanup finished a full pass for this account. */
  legacyCalendarCleanupDoneAt?: number;
  /** Resume position of an unfinished legacy calendar cleanup (opaque silo cursor). */
  legacyCalendarCleanupCursor?: string;
  legacyCalendarCleanupCursorAt?: number;
  /** Last calendar discovery forced to a full read for the cleanup (at most one per 24h). */
  legacyCalendarCleanupForcedFullReadAt?: number;
  contactCursorResetAt?: number;
  projectTaskCheckpoint?: {
    cursor: string | null;
    watermark: number | null;
    lastFullReconcileAt?: number;
    continuationRequired?: boolean;
  };
  pendingProjectTaskCheckpoint?: {
    cursor: string | null;
    watermark: number | null;
    generation: string;
    hasMore: boolean;
    lastFullReconcileAt?: number;
  };
  providerApiBaseUrl?: string;
  /** Bounded non-secret account metadata used by provider adapters. */
  providerContext?: Record<string, string>;
  providerOrganizationId?: string;
  providerUserId?: string;
  providerEnvironment?: "production" | "sandbox";
  contactWebhookSubscriptionId?: string;
  contactWebhookClientState?: string;
  contactWebhookTenantId?: string;
  contactWebhookResourceUserId?: string;
  contactWebhookResource?: string;
  contactWebhookNotificationUrl?: string;
  reauthorizeRequired?: boolean;
  isSynced?: boolean;
  selectedApps?: string[];
  grantedScopes?: string[];
  requestedScopes?: string[];
  providerOwnerApproval?: {
    approvedBy?: string;
    approvedAt?: number;
    configurationGeneration?: string;
    policyReviewedAt?: number;
    pricingReviewedAt?: number;
    accountOwnershipReviewedAt?: number;
    readEnabled?: boolean;
    outboundEnabled?: boolean;
    approvedCapabilities?: string[];
    maxProviderRequestsPerSyncRun?: number;
  };
  providerRuntimeEvidence?: Array<{
    provider?: string;
    providerAccountKey?: string;
    configurationGeneration?: string;
    semanticOperation?: string;
    evidenceKind?:
      | "provider_read"
      | "helper_exchange"
      | "webhook_challenge"
      | "verified_webhook_delivery"
      | "provider_write_ack"
      | "oauth_identity"
      | "embed_player"
      | "portability_import"
      | "subscription_registration";
    evidenceId?: string;
    evidenceFingerprint?: string;
    environmentFingerprint?: string;
    grantedScopes?: string[];
    observedAt?: number;
    recordedAt?: number;
  }>;
  cloudDocumentConfiguration?: Record<string, unknown>;
  cloudDocumentSetup?: Record<string, unknown>;
  providerAuthMode?: "legacy" | "community";
  credentialKind?: string;
  botUsername?: string;
  deliveryMode?: string;
  outboundEnabled?: boolean;
  dataUseConsent?: {
    version: string;
    selectedResources: string[];
    acknowledgedAt: number;
  };
  portabilityImport?: {
    importId?: string;
    status?:
      | "queued"
      | "requesting"
      | "waiting"
      | "storing"
      | "completed"
      | "error";
    completedResources?: number;
    resourceCount?: number;
    parsedFacts?: number;
    storedFacts?: number;
    warningCount?: number;
    message?: string;
    requestedResources?: string[];
    grantedResources?: string[];
    missingResources?: string[];
    emptyResources?: string[];
    preferenceEvidence?: {
      consentVersion?: string;
      generatedAt?: string;
      factCounts?: Record<string, number>;
      repeatedChannels?: Array<{
        title: string;
        evidenceCount: number;
      }>;
      playlistTitles?: string[];
      limitations?: string[];
    };
    updatedAt?: number;
  };
  // iCloud helper app specific fields
  helperConnected?: boolean;
  helperStatus?: string;
  lastStatusUpdate?: number;
  permissions?: {
    calendar?: boolean;
    contacts?: boolean;
    reminders?: boolean;
    notes?: boolean;
    mail?: boolean;
    icloudDrive?: boolean;
    messages?: boolean;
  };
  // Sync stats for iCloud messages
  syncStats?: {
    totalConversations?: number;
    totalMessages?: number;
    messagesSynced?: number;
    attachmentsSynced?: number;
    messagesProcessed?: number;
    errors?: number;
    lastSync?: number;
  };
  // Slack-specific fields
  user_id?: string;
  team_id?: string;
  team_name?: string;
  // Discord-specific fields
  botId?: string | number;
  // WhatsApp linked-device (unofficial companion) fields.
  // Linking uses a WhatsApp Web session via a companion process, which is
  // against WhatsApp's Terms of Service and can get the user's number banned.
  // We only link after explicit, logged acknowledgement of that risk.
  acknowledgedBanRisk?: boolean;
  acknowledgedBanRiskAt?: number;
  // WhatsApp companion states plus the provider-record account states
  // ("configured" after owner setup, "active" once a first read succeeded).
  // "owner_rejected" is what an owner Disconnect leaves: the row keeps its
  // identity and history but holds no account until the owner connects again.
  linkStatus?: "unlinked" | "pairing" | "linked" | "error" | "configured" | "active" | "owner_rejected";
  linkedPhoneNumber?: string;
  linkedAt?: number;
  companionConnected?: boolean;
  lastCompanionHeartbeat?: number;
};

// User preferences type - stored in separate search-user-preferences index
export type UserPreferencesType = {
  language: string;
  micState: "Checking Microphone..." | "Microphone Permission Denied" | "Microphone Unavailable" | "Microphone Available" | "Listening..." | "Stopped Listening" | "Processing...";
  aiVoice: string;
  // Optional AI personalization fields
  aiName?: string;
  aiPersonality?: string;
  isSpeakerON: boolean;
  isMicrophoneON: boolean;
  // Keyboard mute preferences
  keyboardMuteEnabled?: boolean;
  keyboardMuteKey?: string; // Canonical chord, e.g. 'Space' or 'Control+Shift+KeyM'.
  disableAutoExitOnInactivity?: boolean;
  /** Desktop only: where new uploads are stored. Absent means "cloud". */
  deviceFileStorage?: "cloud" | "device";
  /** Explicitly closed prepared window; saved windows and new updates still restore. */
  notificationWindowDismissed?: boolean;
  workspaceScenes?: import("@/src/features/workspace/sceneCatalog").WorkspaceSceneCatalog;
  /** User-level switch for contributing scrubbed generic procedures to commons. */
  commonsPlaybookContributionEnabled?: boolean;
  assistantPhoneNumber?: string;
  assistantPhoneNumberId?: string;
  outboundCallerNumber?: string;
  outboundCallerNumberStatus?: "pending" | "verified";
  outboundCallerNumberVerifiedAt?: number;
  outboundCallerNumberVerificationStartedAt?: number;
  outboundCallerNumberVerificationMethod?: "sms" | "call";
  outboundCallerNumberVerificationProof?: string;
  componentsMap: Array<componentsMapType | ComponentMapReferenceType>;
  // The user's IANA zone (e.g. 'America/New_York'). Absent until signup or
  // /api/preferences/timezone captures it: never default it to a guess.
  // Servers read it through resolveCalendarTimezone; clients fall back to the
  // device zone.
  timezone?: string;
  // How `timezone` was set: "auto" (captured from the browser) may be
  // silently re-captured when the browser disagrees; "manual" (user-set)
  // is never overwritten automatically.
  timezoneSource?: "auto" | "manual";
  notificationPreferences?: {
    byType?: Record<string, { muted?: boolean; snoozedUntil?: number }>;
  };
  /** @deprecated Scheduling now derives adaptive availability from memory and live context. */
  workHours?: {
    startHour: number;
    startMinute?: number;
    endHour: number;
    endMinute?: number;
    dayStartHour?: number;
    dayStartMinute?: number;
    dayEndHour?: number;
    dayEndMinute?: number;
    timezone: string;
    days: string[];
  };
  // Last known layout mode - used to restore layout state on page load
  // Note: The actual isMobileLayout state is determined by device detection,
  // but this preserves the last mode for edge cases
  lastLayoutMode?: 'desktop' | 'mobile';
  onboarding?: import("../../features/onboarding/preferences").OnboardingPreference;
  /** Living theme: how each phase of the day looks (Settings → Look and feel, voice). */
  themePrefs?: import("@smartmanager/shell-ui/features/theme/livingTheme").ThemePrefsV2;
  [key: string]: any; // You can add other preference fields here
};

export type UserLocationType = {
  lat: number;
  lng: number;
  /** When the device took the fix (epoch ms, device clock). */
  capturedAt?: number;
  /**
   * How old the fix was when the client built the turn, measured on the
   * device's clock. Current web clients send this, not `capturedAt`.
   */
  ageMs?: number;
} | null;

// Subscription plan types for storage limits
export type SubscriptionPlanType = import(
  "../../features/billing/plans"
).SubscriptionPlanType;
export type StoredSubscriptionPlan = import(
  "../../features/billing/plans"
).StoredSubscriptionPlan;

// Storage usage tracking for individual users
export type UserStorageUsageType = {
  totalStorageBytes: number;     // Total storage used by this user in bytes
  totalDocCount: number;         // Number of documents uploaded by this user
  lastUpdated: number;           // Timestamp of last storage update
};

export type PassageUserType = {
  device: string;
  /** Read-only echo of the account's compliance mode (server-resolved; never trusted from the client). */
  complianceMode?: "standard" | "hipaa";
  userId?: string;
  publicId: string;
  chatId: string;
  companyId?: string;
  isAuthorized: boolean;
  lastSignedInAt?: number;
  backgroundTasksDisabled?: boolean;
  disabledBackgroundTaskIds?: string[];
  field: string;
  profession: string;
  dateOfBirth: {
    Day: number;
    Month: number;
    Year: number;
  };
  firstName: string;
  lastName?: string;
  email: string;
  /**
   * Staff marker the Control Plane mirrors from the `internalMemberGrants`
   * ledger (`convex/internalMembers.ts`). Read-only for the app; no email
   * domain sets it.
   */
  internalMember?: boolean;
  /**
   * Response-only account entitlement snapshot for native clients, derived
   * from the server's entitlement decision. Never persisted or read back.
   */
  accountEntitlement?: import("@/src/features/billing/accountEntitlement").AccountEntitlementSnapshot;
  phone: string;
  hasNickname: boolean;
  nickName: string;
  image: string;
  // Preferences are stored in separate index (search-user-preferences) and merged at runtime
  preferences: UserPreferencesType;
  sessionTokens: {
    [key: string]: SyncVendorProp;
  };
  createdDate: number;
  trialStartedAt?: number;
  role: string;
  environment: string;
  startPresentation?: boolean;
  companyName?: string;
  website?: string;
  staff?: string;
  payments?: {
    subscriptionStatus: string;
    subscriptionId: string;
    plan?: StoredSubscriptionPlan; // pro or lifetime; legacy "free" grants nothing (normalizeSubscriptionPlan)
    customerId?: string;          // Stripe customer id for invoice retrieval
    stripeCustomerId?: string;    // Legacy Stripe customer id field
    billingPlan?: import("@/src/features/billing/plans").BillingPlanKey; // Paid plan the verified subscription buys
    storageAddonUnits?: number;   // Vault storage add-on units (200 GB each) on the subscription
  };
  // Storage usage tracking for the user
  storageUsage?: UserStorageUsageType;
  memory: string;
  hasBookedMeeting?: boolean;
  meetingDetails?: {
    date: string;
    time: string;
    eventId: string;
  };
  location?: UserLocationType;
  selectedPlan?: import("@/src/features/billing/plans").BillingPlanKey;
  billingCycle?: "monthly" | "yearly";
  legalAcceptance?: import("@/src/features/legal/legalDocuments").LegalAcceptanceSummary;
  accountDeletionStatus?: "in_progress" | "deleting" | "blocked";
  accountDeletionStartedAt?: number;
  accountDeletionError?: string;
  accountDeletionExportKey?: string;
  accountDeletionAttemptId?: string;
};

export type PatientType = {
  userID: string;
  name: string;
  age: string;
  gender: string;
  "date of birth": string;
  occupation: string;
  "insurance provider": string;
  "insurance id": string;
  "referring physician": string;
  "Preferred Language": Array<string>;
  Allergies: Array<string>;
  "Medical History": Array<string>;
};

export type StaffMembersType = {
  uploadedFiles: number;
  voiceChars: number;
  aiTokens: number;
  userID?: string;
  firstName: string;
  expenses: number;
  lastName?: string;
  email?: string;
  role?: 'AccountOwner' | 'admin' | 'member';
  joinedDate?: number;
};

// Team invite for inviting users to join a company
export type TeamInvite = {
  token: string;
  companyId: string;
  inviterId: string;
  inviterEmail: string;
  inviterName: string;
  role: 'member' | 'admin';
  email?: string | null; // Optional: restrict to specific email
  expiresAt: number;
  usedAt?: number | null;
  usedBy?: string | null;
  createdAt: number;
};

export type TaxesInfoType = {
  name: string;
  value: number;
};

type PaymentInfoType = {
  isRecurring: boolean;
  taxes: TaxesInfoType[];
};

export type invoicesType = {
  s3URL: string;
  referencedMonth: string;
  createdDate: number;
  dueDate: string;
  paidOn: string;
  paidAmount: number;
  currency?: string;
  id?: string;
  number?: string;
  /** Stripe invoice status: draft | open | paid | uncollectible | void. */
  status?: string;
  subtotal?: number;
  total?: number;
  amountDue?: number;
  amountRemaining?: number;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
};

export type CompanyDetailsType = {
  companyName: string;
  companyIndustry?: string;
  companyWebsite: string;
  companyAddress: string;
  companyPhoneNumber: string;
  staffMembers: StaffMembersType[];
  paymentInfo: PaymentInfoType;
  invoices: invoicesType[];
  createdDate: number;
  companyLogoUrl: string;
  [key: string]: any;
};

export type ChatSession = {
  chatHistory: ChatType[];
  messages?: any;
  summary?: string;
  runInitialSurvey?: boolean;
  surveyProgress?: {
    nameCollected: boolean;
    dailyRoutineAnswered: boolean;
    emailPrioritiesAnswered: boolean;
    calendarManagementAnswered: boolean;
    taskOrganizationAnswered: boolean;
    personalContextAnswered: boolean;
    painPointsAnswered: boolean;
    integrationNeedsAnswered: boolean;
    subscriptionInfoShown: boolean;
    currentStage: number;
    userName: string;
    userNickName: string;
    isComplete: boolean;
  };
};

// Structured source citation with extracted evidence sentences
export type StructuredSource = {
  id: number;
  title: string;
  date?: string;
  type: 'document' | 'web' | 'email' | 'photo' | 'memory' ;
  url?: string;
  docId?: string;
  fileName?: string;
  imageURI?: string; // S3 key for the file
  pageNumber?: number;
  extractedSentences: string[];
};

/**
 * Where a turn's question text came from. Only "typed" (chat input) and
 * "voice" (voice turn manager) are the user's own words; one-click sends that
 * can carry third-party text are "ui_template". Absent means ui_template.
 */
export type ChatQuestionSource = "typed" | "voice" | "ui_template";

export type ChatType = {
  chatId: string;
  reactId: string;
  question: string;
  questionSource?: ChatQuestionSource;
  originalQuestion?: string;
  rewrittenQuestion?: string;
  inputType?: "text" | "voice";
  queryRewrite?: any;
  textAnswer?: string; // Text response optimized for reading/display
  audioAnswer?: string; // Audio response optimized for TTS/speech
  /** `"text"`: captions show textAnswer, since audioAnswer only spells it for the voice. */
  answerCaption?: "text";
  audioCompletionKey?: string; // Distinguishes independently completed workflow utterances
  /** Client presentation metadata; never authorization to execute an action. */
  restoredAudioCompletionKeys?: string[];
  restoredAudioAnswer?: { text?: string; updatedDate?: number; completionRevision?: number };
  completionRevision?: number; // Saved revision used to reject stale completion responses
  /** True while the turn shows an acknowledgement or progress line and its answer is still coming. */
  requestPending?: boolean;
  tokens: number;
  voiceChars: number;
  updatedDate: number;
  createdDate: number;
  fetchedData: any[];
  render: string;
  feedbackStatus: string;
  summary: string;
  attachments: { fileName: string; date: number; documentId?: string }[];
  announcements: string[];
  isVoiceInput: boolean;
  emotionalAnalysis: any; // TODO: add type
  sources: StructuredSource[];
  voiceDescription: string;
  surveyProgress?: any;
  isNotification?: boolean;
  // Internal context data for AI - not displayed in chat UI
  contextData?: {
    type: 'todo_verification' | 'subtask_verification' | 'subtasks_batch_processing' | 'subtask_processing' | 'general';
    todoId?: string;
    todoTitle?: string;
    todoDescription?: string;
    subtaskIndex?: number;
    subtaskTitle?: string;
    subtaskDescription?: string;
    urgency?: string;
    dueDate?: string;
    allSubtasks?: Array<{ title: string; description?: string; done?: boolean }>;
    source?: string;
    senderFrom?: string;
    context?: string;
    fetchedData?: any;
    // Set when a component built the question, or when the turn answers an
    // approval tap (the question is the button's label); never user-authored.
    questionOrigin?: "component" | "approval";
  };
};

export type UserDetailsType = {
  name: string;
  phone: string;
  nickName: string;
  profession: string;
  dateOfBirth: { Day: number; Month: number; Year: number };
  userImageUrl: string;
};

export type DocumentEmbeddingType = {
  id: string;
  text: string;
  companyId: string;
  title: string;
  uploadedBy: string;
  fileType: string;
  gpt_query_cosine_search: {
    predicted_value_f32: number[];
  };

  [key: string]: any;
};

export type EmailEmbeddingType = {
  id: string;
  text: string;
  companyId: string;
  uploadedBy: string;
  fileType: string;
  gpt_query_cosine_search: {
    predicted_value_f32: number[];
  };
  body: string;
  createdDate: number;
  docId: string;
  from: string;
  to: string;
  subject: string;
  threadId: string;
  attachments: {
    attachmentId: string;
    filename: string;
    mimeType: string;
    size: number;
  };
  tokenCount: number;
  status: string;

  [key: string]: any;
};

interface EmergencyContact {
  name: string;
  phoneNumber: string;
  relationship: string;
}

interface Employer {
  name: string;
  address: string;
  phoneNumber: string;
  email: string;
}

interface InsurancePolicyHolder {
  name: string;
  address: string;
  phoneNumber: string;
  email: string;
}

export interface IndustryFormProps {
  handleCompanyChange: (value: { companyIndustry: string }) => void;
}

export interface CompanyDetailsProps {
  initData: CompanyDetailsType;
  handleCompanyChange: (newValues: CompanyDetailsType) => void;
}

interface HealthcareProviders {
  primaryCarePhysician: string;
  referringPhysician: string;
  otherSpecialistDoctors: { name: string }[];
}

interface MedicalHistory {
  diagnoses: { date: string; diagnosis: string }[];
  surgicalHistory: { date: string; procedure: string }[];
  familyMedicalHistory: { relationship: string; condition: string }[];
  socialHistory: {
    smoking: string;
    alcoholUse: string;
    drugUse: string;
    lifestyle: string;
  };
  medications: {
    medication: string;
    dosage: string;
    frequency: string;
    date: string;
    response: string;
  }[];
  allergicReactions: { allergy: string; reaction: string }[];
  womenHealth: {
    pregnancies: number;
    liveBirths: number;
    abortions: number;
    menopausalStatus: string;
  };
}

interface AssessmentPlan {
  visits: { date: string; reason: string; provider: string }[];
  results: { observation: string; temperature: string; test: string; result: string }[];
  treatmentPlan: string;
  followUpRecommendations: string;
  mentalHealthAssessments: string;
}

interface ImmunizationRecords {
  vaccines: { type: string; date: string; provider: string }[];
}

interface TreatmentPlans {
  current: { treatment: string; goal: string }[];
  past: { treatment: string; date: string; notes: string }[];
  progressNotes: { treatment: string; date: string; notes: string }[];
}

interface ConsentsAuthorizations {
  consentForTreatment: { form: string; date: string; provider: string }[];
  privacyNoticeAcknowledgments: { form: string; date: string; provider: string }[];
  authorizationReleaseInformation: { form: string; date: string; provider: string }[];
  livingWill: string;
  durablePowerOfAttorney: string;
  doNotResuscitate: string;
}

interface BillingPayment {
  servicesRendered: { service: string; date: string; cost: string }[];
  paymentsReceived: { paymentDate: string; amount: string }[];
  outstandingBalance: string;
  itemizedBills: { service: string; date: string; cost: string }[];
  explanationOfBenefits: string;
  paymentPlanDetails: string;
}

interface LaboratoryImagingResults {
  bloodTests: { test: string; date: string; result: string }[];
  urinalysis: { test: string; date: string; result: string }[];
  imaging: {
    xraysMRIsCTScans: { imagingType: string; date: string; result: string }[];
    ultrasounds: { imagingType: string; date: string; result: string }[];
  };
  pathologyReports: { test: string; date: string; result: string }[];
}

interface PhysicalExaminationRecords {
  weightHeight: {
    weight: string;
    height: string;
  };
  bodyMassIndex: string;
  bloodPressure: string;
  pulseRespiratoryRates: {
    pulse: string;
    respiratoryRate: string;
  };
  generalAppearance: string;
  systemExaminations: {
    cardiovascular: string;
    respiratory: string;
    gastrointestinal: string;
    musculoskeletal: string;
    neurological: string;
    skin: string;
  };
}

interface LegalDocuments {
  procedureConsentForms: { form: string; date: string; provider: string }[];
  guardianshipCustodialInfo: string;
  workersCompensationClaims: string;
}

export type PatientFileType = {
  patientUUID: string;
  companyId: string;
  createdDate: number;
  createdBy: string;
  tokens: number;
  personalDemographics: {
    name: string;
    lastname: string;
    dateOfBirth: string;
    gender: string;
    address: string;
    phoneNumber: string;
    email: string;
    emergencyContact: EmergencyContact;
    maritalStatus: string;
    occupation: string;
    raceEthnicity: string;
    preferredLanguage: string;
    socialSecurityNumber: string;
    organDonorStatus: string;
    employer: Employer;
  };
  insurance: {
    provider: string;
    policyNumber: string;
    policyHolder: InsurancePolicyHolder;
    coverageDetails: string;
    preAuthorization: string;
    coPayDeductible: string;
    secondaryInsurance: string;
  };
  healthcareProviders: HealthcareProviders;
  medicalHistory: MedicalHistory;
  assessmentPlan: AssessmentPlan;
  immunizationRecords: ImmunizationRecords;
  treatmentPlans: TreatmentPlans;
  consentsAuthorizations: ConsentsAuthorizations;
  billingPayment: BillingPayment;
  laboratoryImagingResults: LaboratoryImagingResults;
  physicalExaminationRecords: PhysicalExaminationRecords;
  legalDocuments: LegalDocuments;
};


interface TTSResponse {
  text: string;
  voiceDescription?: string;
}

export type Memory = {
  id: string;
  source: string;
  type: 'observation' | 'insight' | 'interest' | 'interests' | '';
  content: string;
  createdDate: number;
  priority: number;
  emotionalIntensity: number;
  topics: string[];
  relationships: {
    primaryContact?: string;  // Contact ID
    relatedContacts: string[]; // Contact IDs
    relationshipContext: string;
  };
  metadata: {
    confidence?: number;
    sourceObservations: string[]; // For insights, references to source observation IDs
    observationType?: string; // For observations: preference, behavior, relationship, fact
    personalizationHints?: { // For observations
      tone: string;
      timing: string;
      content: string;
      relationship: string;
    };
    personalizationStrategies?: { // For insights
      communication: string;
      timing: string;
      content: string;
      relationship_building: string;
      decision_making: string;
    };
    reasoning?: string; // Explanation of the insight/pattern
    timespan?: {
      start: number;
      end: number;
    };
    // Extended governance and traceability fields
    model?: string;
    embeddingVersion?: string;
    sourceId?: string;
    sourceType?: string;
    clusterId?: string;
    lastUpdated?: number;
    isHypothesis?: boolean;
    supportCount?: number;
    quality?: number;
  };
  gpt_query_cosine_search: {
    predicted_value_f32: number[];
  };
  companyId: string;
  userId: string;
};

export type MemoryFileCategory =
  | "identity_profile"
  | "communication_preferences"
  | "work_style"
  | "projects_and_goals"
  | "relationships"
  | "scheduling_preferences"
  | "interests"
  | "constraints"
  | "brand_voice";

export type TemporaryFact = {
  content: string;
  expiresAt: number;
  createdAt: number;
  lastUsedAt?: number;
  timesUsed?: number;
  tier?: MemoryProvenanceTier;
  /**
   * The writer that stored it, when a deterministic writer (the user's linked
   * agent) did. Model-written temporaries carry none. Q5: an outside writer
   * may change or remove only a temporary fact with its own source.
   */
  source?: string;
};

// Provenance for one active fact. "Confirmed" means the fact survived a
// canonical rewrite against new evidence; "used" means the final answer model
// attributed its synthesized response to this exact fact.
export type MemoryClaimBasis = "confirmed" | "inferred";

// Who a remembered fact came from. "user": the user typed or said it.
// "own": the user's own sent mail or actions. "outside": received email,
// documents, web, other people. Outside facts are quarantined: kept, never
// loaded as instructions, and only shown labeled as outside claims.
export type MemoryProvenanceTier = "user" | "own" | "outside";

// A standing weekly time rule read out of one remembered fact, in the user's
// own wall clock. "avoid": never book anything then ("2 to 4 pm is school
// pickup"). "working_hours": book work only inside it. days are 0 = Sunday
// to 6 = Saturday. endMinute <= startMinute crosses midnight into the next day.
export type SchedulingBoundaryKind = "avoid" | "working_hours";

export type SchedulingBoundary = {
  kind: SchedulingBoundaryKind;
  days: number[];
  startMinute: number;
  endMinute: number;
};

export type MemoryFactRecord = {
  text: string;
  source: string;
  sourceRef?: string;
  sourceRefs?: string[];
  basis?: MemoryClaimBasis;
  tier?: MemoryProvenanceTier;
  firstSeenAt: number;
  lastConfirmedAt: number;
  timesConfirmed: number;
  lastUsedAt?: number;
  timesUsed?: number;
  // The time rules this fact states, read once when the fact was written, so
  // forgetting or undoing the fact removes them with it. [] means the fact
  // was read and states none; absent means it was never read.
  timeBoundaries?: SchedulingBoundary[];
  // The people this fact is about, other than the user, read once by a model
  // when the fact was saved and matched to the names memory already knows
  // ("Dana" becomes the contact "Dana Okafor"). [] means the fact was read
  // and names nobody; absent means it was never read.
  people?: string[];
};

// When a stale fact was first seen stale (G-RET-5: purged 12 months later).
export type MemoryStaleFactRecord = {
  text: string;
  staledAt: number;
};

export type MemoryFile = {
  id: string;
  type: "memory-file";
  userId: string;
  companyId: string;
  category: MemoryFileCategory;
  title: string;
  summary: string;
  content: string;
  tags: string[];
  activeFacts: string[];
  // Parallel provenance for activeFacts, keyed by normalized fact text.
  // Maintained mechanically on merge — the LLM never writes this field.
  factRecords?: MemoryFactRecord[];
  staleFacts: string[];
  // Parallel to staleFacts; maintained mechanically on save, never by the LLM.
  staleFactRecords?: MemoryStaleFactRecord[];
  openQuestions: string[];
  temporaryFacts: TemporaryFact[];
  relatedContacts: string[];
  sourceTypes: string[];
  // Least-trusted provenance tier of any write that shaped summary/content.
  // Outside-tainted narrative is withheld from prompts (facts render by tier).
  contentTier?: MemoryProvenanceTier;
  createdDate: number;
  updatedDate: number;
  revision: number;
  lastReflectedAt?: number;
  // Set when the user's own "forget" emptied the file. The emptied file is
  // held (invisible to every read) so the forget can be undone, and the
  // retention sweep removes it once the undo window has passed. Any write
  // that puts a fact back clears it.
  forgottenAt?: number;
  // The last change the user can still take back. Server-only; see MemoryUndoJournal.
  undo?: MemoryUndoJournal;
  // File/category selection is tracked separately from fact-level usage.
  // Marks are throttled to one per file per hour, so timesSelected counts
  // hours with a selection and lastSelectedAt can trail the latest one.
  lastSelectedAt?: number;
  timesSelected?: number;
  // Voyage embedding of the file's title/summary/facts (full-precision
  // floats), written on every save; used for semantic retrieval ranking.
  // Stripped from memory payloads returned to prompts/responses.
  gpt_query_cosine_search?: { predicted_value_f32: number[] };
};

// A file's content state, as one change found it.
export type MemoryUndoSnapshot = Pick<
  MemoryFile,
  | "summary" | "content" | "contentTier" | "tags" | "activeFacts" | "factRecords" | "staleFacts"
  | "staleFactRecords" | "openQuestions" | "temporaryFacts" | "relatedContacts" | "sourceTypes"
>;

// The last user-visible change to a memory file (a correct or forget in the
// Memory field, or update_memory), kept on the file so it can be undone:
// provenance records and temporary expiries come back as they were, never
// rebuilt. The lists are what the client was told changed; an undo that names
// anything else is refused. Server-only: stripped from every read that leaves
// the memory module, never embedded or sent to a model. A later write that
// leaves the change's facts as it made them keeps it in force, rebased so the
// undo takes back only this change; a write that touches one of those facts
// drops it. The retention sweep strips it once MEMORY_FORGET_UNDO_WINDOW_MS
// has passed, so forgotten text lingers at most that long.
export type MemoryUndoJournal = {
  // Revision the change produced: what the client's undo expects.
  revision: number;
  // Revision it still applies to. Later writes that leave the change's facts
  // alone move it forward.
  appliesTo: number;
  at: number;
  replaced: Array<{ current: string; previous: string }>;
  added: string[];
  removed: string[];
  removedTemporary: string[];
  // Suppressions this change created; only these are lifted by its undo.
  suppressed: string[];
  // What the undo restores: the file as it was before the change, or, once a
  // later write moved the file on, that file with only this change taken back.
  before: MemoryUndoSnapshot;
};

// One remembered conversation moment (episodic memory). Stored alongside
// memory files in `search-memory`, retrieved by time-decayed semantic
// similarity to answer "what did we discuss about X?" style recall.
export type MemoryEpisode = {
  id: string;
  type: "memory-episode";
  userId: string;
  companyId: string;
  content: string;
  relatedContacts: string[];
  chatId?: string;
  verification?: "unverified_conversation_record";
  // "outside" when the answer was derived from fetched outside content.
  tier?: MemoryProvenanceTier;
  createdDate: number;
  gpt_query_cosine_search?: { predicted_value_f32: number[] };
};

export type MemoryFilePatch = {
  factSources?: Array<{ fact: string; sourceRefs: string[]; basis?: MemoryClaimBasis }>;
  category: MemoryFileCategory;
  summary: string;
  content: string;
  tags: string[];
  activeFacts: string[];
  staleFacts: string[];
  openQuestions: string[];
  temporaryFacts: Array<{
    content: string;
    expiresAt: string | number;
  }>;
};

export type ContactTier = "priority" | "important" | "standard";

export type ContactInstruction = {
  instruction: string;
  expiresAt?: number;
  expiresAfterEvent?: string;
  createdAt: number;
  /** Set only by code after the text is grounded in the user's own request. */
  setBy?: "user";
};

export type ContactTierMetadata = {
  tier: ContactTier;
  tierSetBy: "user" | "system";
  tierSetAt: number;
  interactionScore: number;
  lastInteractionAt: number;
  meetingFrequency: number;
  emailFrequency: number;
  responseLatency: number;
  customInstructions: ContactInstruction[];
};

export type Contact = {
  isDeleted: boolean;
  names?: {
    displayName?: string;
    givenName?: string;
    familyName?: string;
    middleName?: string;
  }
  emailAddresses?: string[];
  phoneNumbers?: {
    value?: string;
    type?: string;
  }[];
  organizations?: Array<{
    name?: string;
    title?: string;
    department?: string;
    jobDescription?: string;
  }>;
  addresses?: string[];
  birthday?:{
      year?: number;
      month?: number;
      day?: number;
    }
  photos?: string[];
  relations?: Array<{
    person?: string;
    type?: string;
  }>;
  urls?: Array<{
    value?: string;
    type?: string;
  }>;
  interests?: string[];
  locations?: string[];
  occupations?:string[];
  skills?: string[];
  gender?: string;
  gpt_query_cosine_search?: {
    predicted_value_f32: number[];
  };
  companyId: string;
  userId: string;
  syncType: string;
  vendor: string;
  lastSynced: number;
  contactMemory?: Partial<Memory>[];
  vendorId: string;
}

// Add MicState type
/** The shell's microphone states, which the voice pipeline drives. */
export type MicState = ShellMicState;

// Pending chat message type for triggering AI verification from other components
/** Only the shell reads it back, so its shape is the shell's. */
export type PendingChatMessage = ShellPendingChatMessage;

// Space calculation types for component placement
export interface FreeSpaceRegion {
  x: number;
  y: number;
  width: number;
  height: number;
  area: number;
  distanceFromDesired?: number;
}

export interface MovementCandidate {
  component: componentsMapType;
  fromPosition: { x: number; y: number };
  toPosition: { x: number; y: number };
  distance: number;
  cost: number;
  relatedComponents: componentsMapType[];
}

export interface PlacementStrategy {
  needsMovement: boolean;
  position: { x: number; y: number } | null;
  componentsToMove: MovementCandidate[];
  freeRegions?: FreeSpaceRegion[];
  // Target bounding box that movements are meant to clear (for verification after movements)
  targetBoundingBox?: { x: number; y: number; width: number; height: number };
  // Whether all blocking components were successfully assigned new positions
  allBlockersResolved?: boolean;
}

// Update AppState type
// Document viewer state types

// Component edit history record for undo/redo
export interface ComponentEditRecord {
  versionId: string;
  componentId: string;
  componentName: string;
  timestamp: number;
  editRequest: string;
  previousState: any;
  newState: any;
  changes: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    description: string;
  }>;
}

interface DocumentViewerState {
  files: any[] | null
  currentDocumentIndex: number
}

interface MediaGalleryState {
  mediaGroups: any[] | null
  currentMediaIndex: number
}

export type ActiveCall = {
  callId: string | null;
  status: "idle" | "ringing" | "connected" | "ended";
  mode: "human";
  phoneNumber: string;
  contactName?: string;
  startTime?: number;
  duration?: number;
} | null;

/**
 * Runtime locator for the native Stream room currently joined in this
 * browser. It deliberately contains no audience or provider claims and is
 * never persisted.
 */
export type JoinedNativeCall = {
  callId: string;
  callType: string;
};

// Per-window dimensions type
export interface WindowDimensions {
  width: number;
  height: number;
}

// Per-window edit history state
export interface WindowEditHistoryState {
  history: ComponentEditRecord[];
  currentIndex: number;
}

// AI activity surfaced on a widget frame: cyan working pulse while the central
// AI acts on a component, gold attention hold when it needs the user, gold
// settle fade when it just finished. Transient — never persisted.
export type AiActivityState = 'working' | 'attention' | 'settled';

export type AppState = {
  // ============================================
  // GLOBAL STATE (shared across all windows)
  // ============================================
  userSession: PassageUserType;
  chatSession: ChatSession;
  isSigningOut: boolean;
  workspaceSaveIssue: "conflict" | "unavailable" | null;
  fileList: any[];
  micState: MicState;
  wakeWordPrompt: string | null;
  isWakeWordListening: boolean;
  isAIPlaying: boolean;
  liveAudioData: Float32Array | undefined;
  // Notifications state
  notifications: any[];
  /**
   * Where the feed read stands this session (memory only). Until the feed has
   * answered, an empty `notifications` means "not loaded yet", never
   * "nothing to show".
   */
  notificationFeedStatus: NotificationFeedStatus;
  /** Rows read on this device the feed does not yet report read (memory only). */
  readNotificationKeys: string[];
  /** Rows marked done the feed may still return (memory only; see `PendingDoneNotificationKeys`). */
  pendingDoneNotificationKeys: Record<string, number>;
  /** Rows taken back with Undo the feed may still leave out (memory only; see `PendingReopenNotificationKeys`). */
  pendingReopenNotificationKeys: Record<string, number>;
  // Pending chat message for AI verification from other components
  pendingChatMessage: PendingChatMessage;
  /** The queued message the composer took and is sending; it holds the slot until settled (chatSlotActions). */
  sendingChatMessage: PendingChatMessage;

  // AI activity per component id, drives the widget frame treatment
  aiActivityByComponent: Record<string, AiActivityState>;

  // Session-only heights of windows folded to one line (see appStore/heldHeights.ts).
  heldComponentHeights: Record<string, import("@/src/state/appStore/heldHeights").HeldComponentHeight>;

  // Component id currently armed by gesture control (gaze/pointer dwell),
  // drives the golden highlight glow on the widget frame
  gestureArmedComponentId: string | null;
  
  // Event notification thresholds tracking (30min, 10min reminders)
  notifiedEventThresholds: Record<string, { notified30min?: number; notified10min?: number }>;
  activeCall: ActiveCall;
  joinedNativeCall: JoinedNativeCall | null;
  
  // Mobile layout mode - when true, components use mobilePosition/mobileSize
  // This is set based on device detection and persisted for consistency
  isMobileLayout: boolean;
  
  // ============================================
  // PER-WINDOW STATE
  // ============================================
  // Current window's ID (set at runtime per-window, NOT persisted)
  currentWindowId: string | null;
  /** Runtime window selections, fenced by owner. Durable default lives in the silo catalog. */
  workspaceScenesByWindow: Record<string, { ownerId: string; sceneId: string | null }>;
  setWorkspaceScene: (windowId: string, sceneId: string | null, ownerId: string, options?: { preserveDocuments?: boolean }) => boolean;
  
  // Components organized by window ID - PRIMARY source of truth
  componentsByWindow: Record<string, componentsMapType[]>;
  
  // Screen dimensions per window
  screenDimensionsByWindow: Record<string, WindowDimensions>;
  
  // QuadTree per window for spatial management (NOT persisted - rebuilt at runtime)
  quadtreesByWindow: Record<string, QuadTree>;
  
  // Deleted components per window (for undo/restore)
  deletedComponentsByWindow: Record<string, componentsMapType[]>;
  
  // Component edit history per window (for undo/redo)
  editHistoryByWindow: Record<string, WindowEditHistoryState>;
  maxEditHistory: number;
  
  // Document viewer state
  documentViewer: DocumentViewerState;
  documentViewerByWindow: Record<string, DocumentViewerState>;

  // Media gallery state
  mediaGallery: MediaGalleryState;

  // ============================================
  // ARTIFACT STORE (single source of truth for AI-produced data)
  // ============================================
  // Artifacts stored once by ID; components hold artifact IDs, never copies.
  artifactsById: Record<string, import("@smartmanager/shell-ui/shared/artifacts").ChainArtifact>;
  upsertArtifacts: (artifacts: import("@smartmanager/shell-ui/shared/artifacts").ChainArtifact[]) => void;
  /** Mint the next version; `reason` says why (see ArtifactProvenance.reason). */
  updateArtifactPayload: (artifactId: string, payload: unknown, revision: { transform: string; reason: string }) => void;
  removeArtifacts: (artifactIds: string[]) => void;
  
  // Debug state
  showQuadTreeOverlay: boolean;
  
  // Core component operations (use current window)
  findNextAvailablePosition: any;
  addComponentToGrid: any;
  removeComponent: any;
  /** Compact active windows only when an explicit tidy action is requested. */
  tidyWorkspace: () => boolean;
  bringComponentToFront: (id: string, options?: { targetWindowId?: string }) => void;
  /** Moves a window and its dock group; `updateUserSession` false keeps the spot for this session only. */
  updateComponentPosition: (
    id: string,
    position: { x: number; y: number },
    updateUserSession?: boolean,
    options?: GeometryChangeOptions,
  ) => void;
  /** Resizes a window and its dock group; `updateUserSession` false keeps the size for this session only. */
  updateComponentSize: (
    id: string,
    size: { width: number; height: number },
    updateUserSession?: boolean,
    options?: GeometryChangeOptions,
  ) => void;
  /**
   * Fold `id` for this session: later size changes stay local and every save keeps its current height.
   * `reserveGrowth` (a fold for a moment) also keeps the space it grows back into free for placement.
   */
  holdComponentHeight: (id: string, options?: { reserveGrowth?: boolean }) => void;
  /** Unfold `id`: its held height comes back at its current width. */
  releaseComponentHeight: (id: string) => void;
  /** End `id`'s hold at its live height: nothing grows back, and saves and syncs take the height it has now. */
  keepHeldComponentHeight: (id: string) => void;
  /** Marks `id` as resized by the user (`userSized`) and saves it; a no-op once marked. */
  markComponentUserSized: (id: string) => void;
  updateComponentGeometry: (
    id: string,
    geometry: {
      position: { x: number; y: number };
      size: { width: number; height: number };
    },
    updateUserSession?: boolean,
    options?: GeometryChangeOptions,
  ) => void;
  dockComponents: (
    contextComponentId: string,
    authoritativeComponentId: string,
    options?: { id?: string; gap?: number; align?: "start" | "center" },
  ) => boolean;
  undockComponent: (componentId: string) => boolean;
  updateComponentProps: (id: string, propsUpdate: Record<string, any>, options?: { targetWindowId?: string }) => void;
  setFileList: any;
  setUserSession: any;
  setChatSession: any;
  resetStore: any;
  
  // ============================================
  // WINDOW MANAGEMENT
  // ============================================
  // Initialize/set current window - MUST be called when window loads
  setCurrentWindowId: (windowId: string) => void;
  initializeWindow: (windowId: string, width: number, height: number) => void;
  
  // Get current window's state
  getComponents: () => componentsMapType[];
  getScreenDimensions: () => WindowDimensions | null;
  getQuadtree: () => QuadTree | null;
  getDeletedComponents: () => componentsMapType[];
  
  // Get state for a SPECIFIC window by ID
  getQuadtreeForWindow: (windowId: string) => QuadTree | null;
  getScreenDimensionsForWindow: (windowId: string) => WindowDimensions | null;
  getComponentsForWindow: (windowId: string) => componentsMapType[];
  
  // Find the best window for placing a new component
  findBestWindowForComponent: (
    width: number,
    height: number,
    preferredPosition?: { x: number; y: number }
  ) => { windowId: string; hasSpace: boolean; position: { x: number; y: number } | null };
  
  // Set current window's dimensions
  setDimensions: (width: number, height: number) => void;
  
  // Cross-window component transfer
  moveComponentToWindow: (
    componentId: string,
    targetWindowId: string,
    newPosition?: { x: number; y: number }
  ) => boolean;
  
  // Get all windows' components (aggregated view)
  getAllComponents: () => componentsMapType[];
  
  // Aggregated quadtree query (checks all windows)
  getAggregatedQuadtreeOverlap: (rect: Rectangle, excludeWindowId?: string) => boolean;
  
  // Rebuild quadtrees
  rebuildQuadtree: (options?: { targetWindowId?: string }) => void;
  rebuildAllQuadtrees: () => void;
  setMicState: (state: MicState) => void;
  setWakeWordPrompt: (prompt: string | null) => void;
  setIsWakeWordListening: (isListening: boolean) => void;
  setActiveCall: (call: ActiveCall) => void;
  setJoinedNativeCall: (call: JoinedNativeCall | null) => void;
  clearJoinedNativeCall: (call: JoinedNativeCall) => void;
  setIsSigningOut: (value: boolean) => void;
  setIsAIPlaying: (state: boolean) => void;
  setLiveAudioData: (audioData: Float32Array | undefined) => void;
  updateComponentScores: (componentScores: Record<string, number>, componentReasons?: Record<string, string>) => void;
  setNotifications: (entries: any[]) => void;
  setAiActivity: (componentId: string, activity: AiActivityState | null) => void;
  setGestureArmedComponentId: (componentId: string | null) => void;
  markNotificationsRead: (keys: string[]) => void;
  /** Drops local read keys: an Undo puts a row that was unread back unread. */
  unmarkNotificationsRead: (keys: string[]) => void;
  /** Hides the rows at once and returns them, so a Done the server refuses can put them back. */
  dismissNotifications: (keys: string[]) => DismissedNotificationEntry[];
  confirmDoneNotifications: (keys: string[], confirmedAt: number) => void;
  /** Takes back a Done the server did not record and puts `restore` back in the list. */
  undismissNotifications: (keys: string[], restore?: readonly DismissedNotificationEntry[]) => void;
  /** Keeps rows taken back with Undo on screen while their `reopen` is in flight. */
  holdReopenedNotifications: (keys: string[]) => void;
  /** The server recorded the reopen; the next feed read started after this drops the keys. */
  confirmReopenedNotifications: (keys: string[], confirmedAt: number) => void;
  /** Drops the hold at once: the reopen was not needed, or the feed refused it. */
  releaseReopenedNotifications: (keys: string[]) => void;
  /** Fills the assistant's question slot only if free (see `chatSlotActions`). */
  queuePendingChatMessage: (message: NonNullable<PendingChatMessage>) => boolean;
  /** Takes the waiting question to send it; it holds the slot until settled. */
  takePendingChatMessage: () => PendingChatMessage;
  settlePendingChatMessage: (message: NonNullable<PendingChatMessage>, outcome: "sent" | "dropped") => void;
  returnPendingChatMessage: (message: NonNullable<PendingChatMessage>) => void;
  withdrawPendingChatMessage: (message: NonNullable<PendingChatMessage>) => boolean;
  chatQuestionStatus: (message: NonNullable<PendingChatMessage>) => "queued" | "sending" | "sent" | "dropped" | "unknown";
  markEventNotified: (eventId: string, threshold: '30min' | '10min') => void;
  markEventsNotified: (events: Array<{ eventId: string; threshold: '30min' | '10min' }>) => void;
  cleanupOldNotificationThresholds: () => void;
  getNotifiedEventThresholds: () => Record<string, { notified30min?: number; notified10min?: number }>;
  calculateDependencyPosition: (
    parentCenterX: number,
    parentCenterY: number,
    dependencyRadius: number,
    minRadius: number,
    maxRadius: number,
    componentWidth: number,
    componentHeight: number,
    options?: { targetWindowId?: string }
  ) => { x: number; y: number };
  // Space-aware placement methods
  // Space analysis and merging methods
  analyzeFreeSpace: (
    componentWidth: number,
    componentHeight: number,
    desiredPosition?: { x: number; y: number },
    options?: { targetWindowId?: string }
  ) => {
    totalFreeArea: number;
    hasContinuousSpace: boolean;
    freeRegions: FreeSpaceRegion[];
    needsMerging: boolean;
    largestSpace: FreeSpaceRegion | null;
  };
  calculateFreeSpaceRegions: (
    componentWidth: number,
    componentHeight: number,
    desiredPosition?: { x: number; y: number }
  ) => FreeSpaceRegion[];
  calculateMovementCost: (
    component: componentsMapType,
    toPosition: { x: number; y: number }
  ) => number;
  findMinimalMovementSet: (
    newComponentWidth: number,
    newComponentHeight: number,
    desiredPosition: { x: number; y: number },
    score?: number,
    options?: { targetWindowId?: string }
  ) => PlacementStrategy;
  executeComponentMovement: (strategy: PlacementStrategy) => void;
  
  // Document viewer actions
  setDocumentFiles: (files: any[], options?: { targetWindowId?: string }) => void;
  setCurrentDocumentIndex: (index: number, options?: { targetWindowId?: string }) => void;
  
  // Media gallery actions
  setMediaGroups: (mediaGroups: any[]) => void;
  setCurrentMediaIndex: (index: number) => void;
  
  // Component restoration. Brings a window back for the person: a minimized
  // one is restored clear of other windows, and an open one that sits where
  // the conversation's transcript opens is moved out of that area.
  // `keepOpenPlacement` leaves an open window exactly where it is (a caller
  // that keeps the person's presentation); a minimized one is still restored.
  restoreComponent: (componentId: string, options?: { targetWindowId?: string; keepOpenPlacement?: boolean }) => void;
  /**
   * Moves an open window (with its dock group) to the nearest free spot when
   * it sits where a self-growing window (the conversation's transcript)
   * opens. Returns whether it moved; a full workspace leaves it in place.
   */
  moveOutOfGrowthPath: (componentId: string, options?: { targetWindowId?: string }) => boolean;
  
  // Dependency spread/collapse
  getAllDescendantDependencyIds: (parentId: string, visited?: Set<string>) => string[];
  toggleDependencySpread: (parentComponentId: string) => void;
  spreadDependencies: (parentComponentId: string) => void;
  collapseDependencies: (parentComponentId: string) => void;
  
  // Debug actions
  toggleQuadTreeOverlay: () => void;
  setQuadTreeOverlay: (value: boolean) => void;

  // Component edit history (undo/redo) - uses current window
  pushComponentEdit: (edit: ComponentEditRecord, options?: { targetWindowId?: string }) => void;
  undoComponentEdit: () => { componentId: string; previousState: any; editRequest: string } | null;
  redoComponentEdit: () => { componentId: string; newState: any; editRequest: string } | null;
  clearComponentEditHistory: () => void;
  getComponentEditHistory: (componentId: string) => ComponentEditRecord[];
  getRecentEditHistory: (count?: number) => Array<{
    componentId: string;
    componentName: string;
    editRequest: string;
    summary: string;
    timestamp: number;
  }>;
  canUndo: () => boolean;
  canRedo: () => boolean;
  applyComponentEdit: (componentId: string, newProps: any, options?: { targetWindowId?: string }) => void;
  
  // ============================================
  // MOBILE LAYOUT MANAGEMENT
  // ============================================
  // Set mobile layout mode (switches between desktop/mobile positions & sizes)
  setMobileLayout: (isMobile: boolean) => void;
  
  // Get effective position for a component based on current layout mode
  // Returns mobilePosition if in mobile mode and available, otherwise regular position
  getEffectivePosition: (component: componentsMapType) => { x: number; y: number };
  
  // Get effective size for a component based on current layout mode
  // Returns mobileSize if in mobile mode and available, otherwise regular size
  getEffectiveSize: (component: componentsMapType) => { width: number; height: number };
  
  // Get components with effective positions/sizes applied for current layout mode
  // This returns a new array with position/size set to the effective values
  getComponentsForCurrentLayout: () => componentsMapType[];
};

export class Rectangle {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;

  constructor(x: number, y: number, w: number, h: number) {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
    Object.freeze(this); // Optional: Makes the instance immutable
  }

  contains(point: { x: number; y: number }): boolean {
    return point.x >= this.x && point.x <= this.x + this.w && point.y >= this.y && point.y <= this.y + this.h;
  }

  overlaps(rect: Rectangle): boolean {
    return !(rect.x >= this.x + this.w || rect.x + rect.w <= this.x || rect.y >= this.y + this.h || rect.y + rect.h <= this.y);
  }

  intersects(rect: Rectangle): boolean {
    return this.overlaps(rect);
  }
}

export class QuadTree {
  boundary: Rectangle;
  capacity: number;
  points: Rectangle[];
  divided: boolean;
  northeast?: QuadTree;
  northwest?: QuadTree;
  southeast?: QuadTree;
  southwest?: QuadTree;

  constructor(boundary: Rectangle, capacity: number) {
    this.boundary = boundary;
    this.capacity = capacity;
    this.points = [];
    this.divided = false;
  }

  insert(rect: Rectangle): boolean {
    if (!this.boundary.overlaps(rect)) {
      return false;
    }

    // If we have children, try to delegate to a child when the rect fully fits
    if (this.divided) {
      const child = this.getFittingChild(rect);
      if (child) {
        return child.insert(rect);
      }
    }

    // Store here if capacity allows or if the rect spans multiple children
    if (this.points.length < this.capacity || !this.canSubdivide()) {
      this.points.push(rect);
      return true;
    }

    // Subdivide and redistribute
    if (!this.divided) this.subdivide();

    // Try to move existing points down if they fully fit in a child
    for (let i = this.points.length - 1; i >= 0; i--) {
      const r = this.points[i];
      const child = this.getFittingChild(r);
      if (child) {
        child.insert(r);
        this.points.splice(i, 1);
      }
    }

    const target = this.getFittingChild(rect);
    if (target) {
      return target.insert(rect);
    }

    // Still spans multiple children, keep at this level
    this.points.push(rect);
    return true;
  }

  subdivide(): void {
    const x = this.boundary.x;
    const y = this.boundary.y;
    const w = this.boundary.w / 2;
    const h = this.boundary.h / 2;

    let ne = new Rectangle(x + w, y, w, h);
    this.northeast = new QuadTree(ne, this.capacity);

    let nw = new Rectangle(x, y, w, h);
    this.northwest = new QuadTree(nw, this.capacity);

    let se = new Rectangle(x + w, y + h, w, h);
    this.southeast = new QuadTree(se, this.capacity);

    let sw = new Rectangle(x, y + h, w, h);
    this.southwest = new QuadTree(sw, this.capacity);

    this.divided = true;
  }

  contains(point: { x: number; y: number }): boolean {
    if (!this.boundary.contains(point)) {
      return false;
    }

    for (let rect of this.points) {
      if (rect.contains(point)) {
        return true;
      }
    }

    if (this.divided) {
      return this.northeast!.contains(point) || this.northwest!.contains(point) || this.southeast!.contains(point) || this.southwest!.contains(point);
    }

    return false;
  }

  overlapsAny(rect: Rectangle): boolean {
    for (let existingRect of this.points) {
      if (existingRect.overlaps(rect)) {
        return true;
      }
    }

    if (this.divided) {
      let hit = false;
      if (this.northeast && this.northeast.boundary.overlaps(rect)) hit = hit || this.northeast.overlapsAny(rect);
      if (this.northwest && this.northwest.boundary.overlaps(rect)) hit = hit || this.northwest.overlapsAny(rect);
      if (this.southeast && this.southeast.boundary.overlaps(rect)) hit = hit || this.southeast.overlapsAny(rect);
      if (this.southwest && this.southwest.boundary.overlaps(rect)) hit = hit || this.southwest.overlapsAny(rect);
      return hit;
    }

    return false;
  }

  remove(rect: Rectangle): boolean {
    if (!this.boundary.overlaps(rect)) {
      return false;
    }

    for (let i = 0; i < this.points.length; i++) {
      if (this.points[i].x === rect.x && this.points[i].y === rect.y && this.points[i].w === rect.w && this.points[i].h === rect.h) {
        this.points.splice(i, 1);
        return true;
      }
    }

    if (this.divided) {
      let removed = false;
      if (this.northeast && this.northeast.boundary.overlaps(rect)) removed = removed || this.northeast.remove(rect);
      if (this.northwest && this.northwest.boundary.overlaps(rect)) removed = removed || this.northwest.remove(rect);
      if (this.southeast && this.southeast.boundary.overlaps(rect)) removed = removed || this.southeast.remove(rect);
      if (this.southwest && this.southwest.boundary.overlaps(rect)) removed = removed || this.southwest.remove(rect);
      return removed;
    }

    return false;
  }

  // Return all stored rectangles, including children (for debug/visualization)
  getAllRectangles(): Rectangle[] {
    let all: Rectangle[] = [...this.points];
    if (this.divided) {
      if (this.northeast) all = all.concat(this.northeast.getAllRectangles());
      if (this.northwest) all = all.concat(this.northwest.getAllRectangles());
      if (this.southeast) all = all.concat(this.southeast.getAllRectangles());
      if (this.southwest) all = all.concat(this.southwest.getAllRectangles());
    }
    return all;
  }

  // Helpers
  private getFittingChild(rect: Rectangle): QuadTree | undefined {
    if (!this.divided) return undefined;
    if (this.northeast && this.fitsWithin(rect, this.northeast.boundary)) return this.northeast;
    if (this.northwest && this.fitsWithin(rect, this.northwest.boundary)) return this.northwest;
    if (this.southeast && this.fitsWithin(rect, this.southeast.boundary)) return this.southeast;
    if (this.southwest && this.fitsWithin(rect, this.southwest.boundary)) return this.southwest;
    return undefined;
  }

  private fitsWithin(a: Rectangle, b: Rectangle): boolean {
    return a.x >= b.x && a.y >= b.y && (a.x + a.w) <= (b.x + b.w) && (a.y + a.h) <= (b.y + b.h);
  }

  private canSubdivide(): boolean {
    return this.boundary.w > 2 && this.boundary.h > 2;
  }
}

// ============================================
// MULTI-WINDOW TYPES
// ============================================

export interface DisplayInfo {
  id: number;
  bounds: { x: number; y: number; width: number; height: number };
  workArea: { x: number; y: number; width: number; height: number };
  isPrimary: boolean;
}

export interface VirtualBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  displays: DisplayInfo[];
}

export interface WindowInfo {
  id: string;
  displayId: number;
  bounds: { x: number; y: number; width: number; height: number };
}

export interface ComponentTransfer {
  component: componentsMapType;
  sourceWindowId: string;
  targetWindowId: string;
  globalPosition: { x: number; y: number };
  localPosition: { x: number; y: number };
}

export interface CrossWindowMessage {
  type: 'COMPONENT_TRANSFERRED' | 'COMPONENT_REMOVED' | 'STATE_SYNC' | 'WINDOW_REGISTERED' | 'WINDOW_CLOSED' | 'DRAG_START' | 'DRAG_END';
  sourceWindowId: string;
  payload: any;
  timestamp: number;
}

export interface MultiWindowState {
  windowId: string | null;
  displayId: number | null;
  virtualBounds: VirtualBounds | null;
  connectedWindows: WindowInfo[];
  isDraggingAcrossWindows: boolean;
  draggedComponent: componentsMapType | null;
}

// ============================================
// PROACTIVE INTELLIGENCE TYPES
// ============================================

/**
 * Permission levels for AI-executed actions
 * Rule: "If it affects others or is hard to undo, ask first"
 */
export type ActionPermissionLevel = 'autonomous' | 'confirm_quick' | 'permission_required';

/**
 * Relevance levels for proactive insights
 */
export type InsightRelevance = 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * Types of contextual checks that can be performed
 */
export type ContextCheckType = 'weather' | 'traffic' | 'weather_destination' | 'documents' | 'contacts' | 'none';

/**
 * Types of proactive insights
 */
export type ProactiveInsightType = 'weather_alert' | 'traffic_warning' | 'preparation_reminder' | 'conflict_detected' | 'opportunity' | 'general_info' | 'deadline_alert' | 'follow_up_tracker' | 'contact_suggestion' | 'meeting_confirmed';

/**
 * Recommended action with permission model
 */
export interface ProactiveAction {
  action: string;
  description: string;
  permissionLevel: ActionPermissionLevel;
  priority: number; // 1-5
  canAutoExecute: boolean;
  itemId?: string;
  riskLevel?: 'low' | 'medium' | 'high';
  affectsOthers?: boolean;
  isReversible?: boolean;
  suggestedConfirmation?: string;
}

/**
 * A proactive insight about an upcoming event
 */
export interface ProactiveInsight {
  eventId: string;
  eventTitle: string;
  insightType: ProactiveInsightType;
  message: string;
  audioMessage: string;
  relevance: InsightRelevance;
  escalate: boolean;
  escalateReason?: string;
  contextData: Record<string, any>;
  recommendedActions: ProactiveAction[];
}

/**
 * Summary of proactive intelligence analysis
 */
export interface ProactiveIntelligenceSummary {
  escalationCount: number;
  highRelevanceCount: number;
  totalInsights: number;
  overallUrgency: 'critical' | 'high' | 'normal' | 'low';
}

/**
 * Enhanced notification response with proactive intelligence
 */
export interface EnhancedNotificationResponse {
  // Standard notification fields
  notifications: any[];
  flattenedNotifications: any[];
  individualSummaries: any[];
  processedSummaries: any[];
  finalSummaryText: string;
  finalSummaryAudio: string;
  isThereNewNotifications: boolean;
  totalUrgentItems: number;
  totalActionItems: number;
  recommendedActions: ProactiveAction[];
  overallTone: string;
  timestamp: number;
  vendorsNeedingReauth: string[];
  
  // Proactive reminder fields
  proactiveReminder: any | null;
  hasProactiveReminders: boolean;
  proactiveReminderText?: string;
  proactiveReminderAudio?: string;
  topPriorityItem?: any;
  topPriorityEvent?: any;
  itemsToMarkNotified?: any[];
  
  // NEW: Proactive Intelligence fields
  proactiveIntelligence?: {
    executionMode: 'recommendations_only';
    insights: ProactiveInsight[];
    summary: ProactiveIntelligenceSummary;
    hasEscalations: boolean;
    escalationMessages: string[];
  } | null;

  recommendedActionExecution: {
    mode: 'recommendations_only';
    executedCount: 0;
  };
  systemMaintenance: {
    calendarDerivedReminders: {
      mode: 'system_maintained';
      resource: 'calendar_derived_reminders';
      trigger: 'notification_refresh';
      status: 'not_run' | 'up_to_date' | 'completed' | 'partial_failure' | 'failed';
      planned: { creates: number; updates: number; deletes: number };
      applied: { creates: number; updates: number; deletes: number };
      failed: { upserts: number; deletes: number };
      createdReminders: Array<{
        id: string;
        prepType: 'join_early' | 'leave_now' | 'prep_check';
        eventId: string;
        eventTitle: string;
        reminderStartTime: number;
        leadTimeMinutes: number;
      }>;
      summary: string;
    };
  };
  
  // NEW: Escalation flag for immediate interrupts
  requiresEscalation?: boolean;
  escalationReason?: string;
}

/**
 * The notification feed read, as every screen sees it: nothing answered yet
 * (`pending`), the last read answered (`ready`), or the last read failed
 * (`error`; `answered` when an earlier read did, so the rows on screen are
 * that answer).
 */
export type NotificationFeedStatus =
  | { state: "pending" }
  | { state: "ready" }
  | { state: "error"; answered: boolean };
